/** DIVETTA MILANO R7 — PDP editorial state choreography.
 *  Purchase zone and native commerce remain owned by ld-pdp-commerce / Horizon.
 */
(() => {
  'use strict';

  const KEY = '__DivettaR7Pdp';
  window[KEY]?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  let rail = null;
  let railController = null;
  let frame = 0;
  let unsubscribeFrame = null;
  let editorState = null;
  let previousActive = null;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (value) => {
    const t = clamp(value);
    return t * t * (3 - (2 * t));
  };

  const routeIsProduct = () => document.body?.dataset.r7Route === 'product';
  /* R2 keeps PDP chapters readable and stable; cinematic scroll ownership is
     reserved for Home/Collection. The legacy state engine remains rollback-safe
     but is deliberately disabled. */
  const enhancedMode = () => false;

  const setAnchors = () => {
    const formula = document.querySelector('.ld-pdp-formula');
    if (formula && !formula.id) formula.id = 'r7-ingredients';
    const howTo = document.querySelector('.ld-pdp-how-to');
    if (howTo && !howTo.id) howTo.id = 'r7-how-to';
    const reviews = document.querySelector('.ld-judgeme-reviews, [data-judgeme-product-reviews]');
    if (reviews && !reviews.id) reviews.id = 'r7-reviews';
  };

  const visibleHeading = (section) => {
    const heading = section.querySelector('h2, h3, [data-r7-pdp-state-label]');
    return heading?.textContent?.replace(/\s+/g, ' ').trim() || '';
  };

  const groupLabel = (section) => {
    const group = section.dataset.r7PdpGroup || 'chapter';
    const labels = {
      formula: 'Formula / Trasparenza',
      why: 'Perché fa per te',
      ritual: 'Rituale / Utilizzo',
      proof: 'Prova / Domande',
      continue: 'Continua il rituale'
    };
    return labels[group] || visibleHeading(section) || 'Capitolo';
  };

  const uniqueStateLabel = (section, index, all) => {
    const base = groupLabel(section);
    const sameGroup = all.filter((candidate) => candidate.dataset.r7PdpGroup === section.dataset.r7PdpGroup);
    if (sameGroup.length <= 1) return base;
    const heading = visibleHeading(section);
    return heading || `${base} ${sameGroup.indexOf(section) + 1}`;
  };

  const stateSections = () => Array.from(document.querySelectorAll('#MainContent [data-r7-pdp-group]'))
    .filter((section) => !section.closest('[hidden]'));

  const sectionWrapper = (section) => section.closest('.shopify-section') || section;

  const revealEditorTarget = (node) => {
    if (!(node instanceof Element)) return;
    const rect = node.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) node.scrollIntoView({ behavior: 'auto', block: 'center' });
  };

  const resetMediaStage = (stage) => {
    stage.removeAttribute('data-r7-media-mounted');
    stage.removeAttribute('data-r7-media-active');
    stage.style.removeProperty('--r7-media-progress');
    stage.querySelectorAll(':scope > [data-r7-media-state]').forEach((state, index) => {
      state.classList.remove('is-active', 'is-dominant', 'is-entering', 'is-leaving');
      state.style.removeProperty('--r7-media-opacity');
      state.style.removeProperty('--r7-media-shift');
      state.style.removeProperty('--r7-media-scale');
      state.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
    stage.querySelectorAll('[data-r7-media-meter]').forEach((meter, index) => {
      meter.classList.toggle('is-active', index === 0);
    });
  };

  const resetSection = (section) => {
    section.classList.remove('is-r7-pdp-state', 'is-r7-pdp-active', 'is-r7-pdp-entering', 'is-r7-pdp-leaving', 'is-r7-pdp-editor-selected');
    section.removeAttribute('data-r7-pdp-state-index');
    section.removeAttribute('data-r7-pdp-state-count');
    section.removeAttribute('data-r7-pdp-state-label');
    section.style.removeProperty('--r7-pdp-state-progress');
    section.style.removeProperty('--r7-pdp-state-presence');
    section.style.removeProperty('--r7-pdp-state-direction');
    section.querySelectorAll('[data-r7-media-stage][data-r7-media-mode="controlled"]').forEach(resetMediaStage);
  };

  const reset = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    document.querySelectorAll('[data-r7-pdp-group]').forEach(resetSection);
    document.body?.classList.remove('r7-pdp-choreography', 'r7-pdp-chapters-in-view');
    document.body?.removeAttribute('data-r7-pdp-active');
    document.body?.removeAttribute('data-r7-pdp-state-count');
    railController?.abort();
    railController = null;
    rail?.remove();
    rail = null;
    instances.clear();
    previousActive = null;
  };

  const mediaBlend = (progress, count) => {
    if (count <= 1) return { from: 0, to: 0, mix: 0, dominant: 0 };
    if (progress >= 1) return { from: count - 1, to: count - 1, mix: 0, dominant: count - 1 };
    const scaled = clamp(progress) * count;
    const from = Math.min(count - 1, Math.floor(scaled));
    const local = clamp(scaled - from);
    if (from >= count - 1 || local <= .72) return { from, to: from, mix: 0, dominant: from };
    const to = Math.min(count - 1, from + 1);
    const mix = smooth((local - .72) / .28);
    return { from, to, mix, dominant: mix < .5 ? from : to };
  };

  const setControlledMedia = (stage, progress) => {
    const states = Array.from(stage.querySelectorAll(':scope > [data-r7-media-state]'));
    if (!states.length) return;
    const blend = mediaBlend(progress, states.length);
    const prior = stage.dataset.r7MediaActive;
    stage.dataset.r7MediaMounted = 'true';
    stage.dataset.r7MediaActive = String(blend.dominant);
    stage.style.setProperty('--r7-media-progress', clamp(progress).toFixed(4));

    states.forEach((state, index) => {
      let opacity = 0;
      let shift = 1.5;
      let scale = 1.012;
      if (index === blend.from) {
        opacity = blend.to === blend.from ? 1 : 1 - blend.mix;
        shift = blend.to === blend.from ? 0 : blend.mix * -1.1;
        scale = 1 + (blend.mix * .01);
      } else if (index === blend.to) {
        opacity = blend.mix;
        shift = (1 - blend.mix) * 1.5;
        scale = 1.012 - (blend.mix * .012);
      }
      const dominant = index === blend.dominant;
      state.style.setProperty('--r7-media-opacity', opacity.toFixed(4));
      state.style.setProperty('--r7-media-shift', `${shift.toFixed(3)}rem`);
      state.style.setProperty('--r7-media-scale', scale.toFixed(4));
      state.classList.toggle('is-active', dominant);
      state.classList.toggle('is-dominant', dominant);
      state.classList.toggle('is-leaving', index === blend.from && blend.to !== blend.from);
      state.classList.toggle('is-entering', index === blend.to && blend.to !== blend.from);
      state.setAttribute('aria-hidden', dominant ? 'false' : 'true');
    });
    stage.querySelectorAll('[data-r7-media-meter]').forEach((meter, index) => {
      meter.classList.toggle('is-active', index === blend.dominant);
    });
    if (prior !== stage.dataset.r7MediaActive) {
      document.dispatchEvent(new CustomEvent('r7:media-state-change'));
    }
  };

  const buildRail = (sections) => {
    rail?.remove();
    if (sections.length < 2) return null;

    const nav = document.createElement('nav');
    nav.className = 'r7-pdp-state-rail';
    nav.setAttribute('aria-label', 'Capitoli del prodotto');
    nav.setAttribute('data-r7-pdp-state-rail', '');
    const list = document.createElement('ol');

    sections.forEach((section, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const number = document.createElement('span');
      const label = document.createElement('b');
      button.type = 'button';
      button.dataset.r7PdpStateTarget = String(index);
      button.setAttribute('aria-label', `Vai a ${section.dataset.r7PdpStateLabel}`);
      number.textContent = String(index + 1).padStart(2, '0');
      label.textContent = section.dataset.r7PdpStateLabel;
      button.append(number, label);
      item.append(button);
      list.append(item);
    });

    nav.append(list);
    railController = new AbortController();
    nav.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-r7-pdp-state-target]');
      if (!button) return;
      const index = Number(button.dataset.r7PdpStateTarget);
      const section = sections[index];
      if (!section) return;
      sectionWrapper(section).scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'center'
      });
    }, { signal: railController.signal });
    document.body.append(nav);
    return nav;
  };

  const setup = () => {
    reset();
    setAnchors();
    if (!enhancedMode()) return;

    const sections = stateSections();
    if (!sections.length) return;
    if (editorState && !sections.includes(editorState)) editorState = null;

    sections.forEach((section, index) => {
      const label = uniqueStateLabel(section, index, sections);
      section.dataset.r7Theme = section.dataset.r7Theme || 'light';
      section.dataset.r7PdpStateIndex = String(index);
      section.dataset.r7PdpStateCount = String(sections.length);
      section.dataset.r7PdpStateLabel = label;
      section.classList.add('is-r7-pdp-state');
      instances.set(section, { index, label });
    });

    document.body.classList.add('r7-pdp-choreography');
    document.body.dataset.r7PdpStateCount = String(sections.length);
    rail = buildRail(sections);
    requestSync();
  };

  const computePresence = (rect, viewport) => {
    const center = rect.top + (rect.height / 2);
    const distance = Math.abs(center - (viewport * .5));
    const range = Math.max(viewport * .82, (rect.height * .45) + (viewport * .34));
    return smooth(1 - clamp(distance / range));
  };

  const sync = () => {
    frame = 0;
    if (!enhancedMode() || !instances.size) return;

    const viewport = Math.max(window.innerHeight, 1);
    const sections = Array.from(instances.keys()).filter((section) => section.isConnected);
    if (!sections.length) {
      setup();
      return;
    }

    const editorLocked = Boolean(editorState && sections.includes(editorState));
    let active = editorLocked ? editorState : null;
    let activeDistance = Number.POSITIVE_INFINITY;
    const rects = new Map();

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      rects.set(section, rect);
      const centerDistance = Math.abs((rect.top + (rect.height / 2)) - (viewport * .5));
      if (!editorLocked && centerDistance < activeDistance) {
        active = section;
        activeDistance = centerDistance;
      }
    });

    const firstRect = rects.get(sections[0]);
    const lastRect = rects.get(sections[sections.length - 1]);
    const chaptersVisible = Boolean(firstRect && lastRect && firstRect.top < viewport * .82 && lastRect.bottom > viewport * .18);
    document.body.classList.toggle('r7-pdp-chapters-in-view', chaptersVisible);

    const activeIndex = active ? Number(active.dataset.r7PdpStateIndex) : 0;
    document.body.dataset.r7PdpActive = String(activeIndex);

    sections.forEach((section, index) => {
      const rect = rects.get(section);
      const rawProgress = clamp(((viewport * .78) - rect.top) / Math.max(rect.height + (viewport * .36), 1));
      const presence = editorState === section ? 1 : computePresence(rect, viewport);
      const isActive = section === active;
      section.style.setProperty('--r7-pdp-state-progress', rawProgress.toFixed(4));
      section.style.setProperty('--r7-pdp-state-presence', presence.toFixed(4));
      section.style.setProperty('--r7-pdp-state-direction', index < activeIndex ? '-1' : index > activeIndex ? '1' : '0');
      section.classList.toggle('is-r7-pdp-active', isActive);
      section.classList.toggle('is-r7-pdp-entering', !isActive && index > activeIndex);
      section.classList.toggle('is-r7-pdp-leaving', !isActive && index < activeIndex);
      section.classList.toggle('is-r7-pdp-editor-selected', editorState === section);
      section.querySelectorAll('[data-r7-media-stage][data-r7-media-mode="controlled"]').forEach((stage) => {
        setControlledMedia(stage, editorState === section ? 0 : rawProgress);
      });
    });

    rail?.querySelectorAll('[data-r7-pdp-state-target]').forEach((button, index) => {
      if (index === activeIndex) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    if (active !== previousActive) {
      previousActive = active;
      document.dispatchEvent(new CustomEvent('r7:pdp-state-change', {
        detail: { index: activeIndex, section: active }
      }));
    }
  };

  const requestSync = () => {
    if (window.DivettaZorge?.requestFrame) {
      window.DivettaZorge.requestFrame();
      return;
    }
    if (frame) return;
    frame = window.requestAnimationFrame(sync);
  };

  const selectedState = (target) => target?.closest?.('[data-r7-pdp-group]')
    || target?.querySelector?.('[data-r7-pdp-group]')
    || null;

  const onEditorSelect = (event) => {
    const state = selectedState(event.target);
    if (!state) return;
    editorState = state;
    if (!instances.has(state)) setup();
    requestSync();
    if (enhancedMode()) {
      revealEditorTarget(sectionWrapper(state));
    }
  };

  const onEditorDeselect = (event) => {
    const state = selectedState(event.target);
    if (!state || editorState === state) editorState = null;
    requestSync();
  };

  const refresh = () => window.requestAnimationFrame(setup);

  document.addEventListener('shopify:section:load', refresh, { signal });
  document.addEventListener('shopify:section:unload', refresh, { signal });
  document.addEventListener('shopify:section:reorder', refresh, { signal });
  document.addEventListener('shopify:section:select', onEditorSelect, { signal });
  document.addEventListener('shopify:section:deselect', onEditorDeselect, { signal });
  document.addEventListener('shopify:block:select', onEditorSelect, { signal });
  document.addEventListener('shopify:block:deselect', onEditorDeselect, { signal });
  window.addEventListener('resize', refresh, { passive: true, signal });
  window.addEventListener('pageshow', refresh, { signal });
  desktopQuery.addEventListener?.('change', setup, { signal });
  reduceMotion.addEventListener?.('change', setup, { signal });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true, signal });
  else setup();

  window[KEY] = {
    init: setup,
    refresh: setup,
    destroy() {
      controller.abort();
      unsubscribeFrame?.();
      reset();
      delete window[KEY];
    }
  };
})();
